import { useState, type FormEvent } from 'react';
import { ApiError, api } from '../api';
import { getBranding } from '../branding';
import { Brand, BrandLinks } from './Brand';
import { Icon } from './Icon';

export type AccountLifecycleFlow =
  | { type: 'activation'; token: string }
  | { type: 'recovery-request' }
  | { type: 'recovery-complete'; token: string }
  | { type: 'email-change'; changeId: string };

function errorCode(reason: ApiError): string {
  const details = reason.details;
  if (!details || typeof details !== 'object') return '';
  const error = (details as { error?: unknown }).error;
  return error && typeof error === 'object' && typeof (error as { code?: unknown }).code === 'string'
    ? (error as { code: string }).code
    : '';
}

export function AccountLifecycle({ flow, onSignIn }: { flow: AccountLifecycleFlow; onSignIn: () => void }) {
  const branding = getBranding();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const title = flow.type === 'activation'
    ? 'Activate your account'
    : flow.type === 'recovery-complete'
      ? 'Choose a new password'
      : flow.type === 'email-change'
        ? 'Verify your new email'
        : 'Reset your password';

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setExpired(false);
    if (flow.type !== 'recovery-request' && flow.type !== 'email-change' && password !== confirmation) {
      setError('Password confirmation does not match.');
      return;
    }
    setBusy(true);
    try {
      if (flow.type === 'activation') await api.activateAccount(flow.token, password);
      else if (flow.type === 'recovery-complete') await api.completePasswordRecovery(flow.token, password);
      else if (flow.type === 'email-change') await api.verifyHostedMemberEmailChange(flow.changeId, code);
      else await api.requestPasswordRecovery(email.trim());
      setSubmitted(true);
    } catch (reason) {
      if (reason instanceof ApiError && errorCode(reason) === 'token_expired') setExpired(true);
      setError(reason instanceof ApiError ? reason.message : 'Unable to complete this request. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const successMessage = flow.type === 'recovery-request'
    ? 'If the address belongs to an eligible account, a recovery link will arrive shortly.'
    : flow.type === 'activation'
      ? 'Your account is active. You can now sign in.'
      : flow.type === 'email-change'
        ? 'Your new email address is active. For security, all dashboard sessions were signed out.'
        : 'Your password has been reset. Other sessions have been signed out.';

  return <main className="login-shell">
    <section className="login-panel" aria-labelledby="lifecycle-title">
      <Brand className="brand--login" />
      <div className="login-heading"><span className="eyebrow">Account security</span><h1 id="lifecycle-title">{title}</h1><p>{flow.type === 'activation' ? 'Set a secure password to finish accepting your invitation.' : flow.type === 'recovery-complete' ? 'Use at least 12 characters for your new password.' : flow.type === 'email-change' ? 'Enter the six-digit code sent to the new address. The code expires after 10 minutes.' : 'Enter your account email and we will send recovery instructions if it is eligible.'}</p></div>
      {submitted ? <div className="lifecycle-success" role="status"><Icon name="check" size={24} /><p>{successMessage}</p><button className="button button--primary button--large" type="button" onClick={onSignIn}>Return to sign in</button></div> : <form className="login-form" onSubmit={submit}>
        {error && <div className="form-alert" role="alert"><Icon name="warning" size={18} /><span>{error}</span></div>}
        {expired && <p className="lifecycle-expired">This link has expired. Return to sign in and request a new recovery link.</p>}
        {flow.type === 'recovery-request' ? <label>Email address<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoFocus /></label> : flow.type === 'email-change' ? <label>Six-digit code<input className="otp-code-input" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" required autoFocus /></label> : <>
          <label>New password<input type="password" autoComplete="new-password" minLength={12} value={password} onChange={(event) => setPassword(event.target.value)} required autoFocus /></label>
          <label>Confirm new password<input type="password" autoComplete="new-password" minLength={12} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required /></label>
        </>}
        <button className="button button--primary button--large" type="submit" disabled={busy || (flow.type === 'email-change' && code.length !== 6)}>{busy ? <><span className="spinner" />Submitting…</> : flow.type === 'recovery-request' ? 'Send recovery link' : flow.type === 'activation' ? 'Activate account' : flow.type === 'email-change' ? 'Verify email' : 'Reset password'}</button>
        <button className="text-button login-forgot" type="button" onClick={onSignIn}>Return to sign in</button>
      </form>}
      <BrandLinks />
    </section>
    <aside className="login-art" aria-hidden="true"><img className="login-art__image" src={branding.assets.loginIllustration} alt="" /></aside>
  </main>;
}
